import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ITenantDetail, TenantFacade, UserFacade } from '@ra-state';
import { IBreadcrumbItem } from '@ra-web-tech-ui-toolkit/components';
import { catchError, combineLatest, filter, map, Observable, of, shareReplay, startWith, switchMap } from 'rxjs';
import { DataService } from './data.service';

const SUB_ORG_PAGE_SIZE = 100;

export interface CommonAdminBreadcrumb {
  items: IBreadcrumbItem[];
  title: string;
}

enum AdminSection {
  Organizations = 'organizations',
  Users = 'users',
  Entitlement = 'entitlement',
  Trials = 'trials',
  SystemAdministration = 'system-administration',
}

// child route segment nested under System Administration
enum SystemAdministrationRoute {
  DeleteServiceBusSubscription = 'delete-service-bus-subscription',
}

const BREADCRUMB_ICON: Record<AdminSection, string> = {
  [AdminSection.Organizations]: 'ra-icon-ide-sm-building-office',
  [AdminSection.Users]: 'ra-icon-ide-sm-user-group',
  [AdminSection.Entitlement]: 'ra-icon-ide-lg-aplications',
  [AdminSection.Trials]: 'ra-icon-ide-md-key-edit',
  [AdminSection.SystemAdministration]: 'ra-icon-ide-sm-system',
};

const STATIC_BREADCRUMB_SECTIONS: Partial<
  Record<AdminSection, { title: string; children?: Record<string, string>; hideOnNestedRoute?: boolean }>
> = {
  [AdminSection.Entitlement]: { title: 'Entitlements' },
  // trial detail/create/reserve pages render their own ra-ui-breadcrumb, so don't duplicate it here
  [AdminSection.Trials]: { title: 'Trial Campaigns', hideOnNestedRoute: true },
  [AdminSection.SystemAdministration]: {
    title: 'System Administration',
    children: {
      [SystemAdministrationRoute.DeleteServiceBusSubscription]: 'Delete Service Bus Subscription',
    },
  },
};

function getStaticBreadcrumb(section: AdminSection, entityId?: string): CommonAdminBreadcrumb | null {
  const staticSection = STATIC_BREADCRUMB_SECTIONS[section];
  if (!staticSection) {
    return null;
  }

  const childTitle = entityId ? staticSection.children?.[entityId] : undefined;
  if (entityId && !childTitle && staticSection.hideOnNestedRoute) {
    return null;
  }

  return {
    // always show the section crumb (with icon); leave title blank when it would just repeat the crumb label
    items: [{ label: staticSection.title, id: `/${section}`, icon: BREADCRUMB_ICON[section] }],
    title: childTitle ?? '',
  };
}

@Injectable({ providedIn: 'root' })
export class BreadcrumbService {
  private readonly router = inject(Router);
  private readonly tenantFacade = inject(TenantFacade);
  private readonly userFacade = inject(UserFacade);
  private readonly dataService = inject(DataService);

  readonly breadcrumb$ = combineLatest([
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      startWith(null),
    ),
    this.tenantFacade.tenantId$,
    this.tenantFacade.tenantDetails$,
    this.userFacade.user$,
  ]).pipe(
    switchMap(([, tenantId, tenant, user]): Observable<CommonAdminBreadcrumb | null> => {
      const [section, encodedEntityId] = this.router.url.split(/[?#]/)[0].split('/').filter(Boolean);
      const entityId = encodedEntityId ? decodeURIComponent(encodedEntityId) : undefined;

      if (section === AdminSection.Organizations) {
        return this.getOrganizationBreadcrumb$(entityId, tenantId, tenant);
      }

      if (section === AdminSection.Users) {
        return of({
          items: [{ label: 'Users', id: `/${AdminSection.Users}`, icon: BREADCRUMB_ICON[AdminSection.Users] }],
          // leave title blank except on the current user's own detail page, to avoid repeating the crumb label
          title: entityId && user.userId === entityId ? user.name : '',
        });
      }

      return of(getStaticBreadcrumb(section as AdminSection, entityId));
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  // appends the parent (or own) organization name to the crumb trail, with a sub-org dropdown when one exists
  private getOrganizationBreadcrumb$(
    entityId: string | undefined,
    tenantId: string,
    tenant: ITenantDetail,
  ): Observable<CommonAdminBreadcrumb> {
    const organizationsCrumb: IBreadcrumbItem = {
      label: 'Organizations',
      id: `/${AdminSection.Organizations}`,
      icon: BREADCRUMB_ICON[AdminSection.Organizations],
    };
    const isCurrentTenant = Boolean(entityId) && tenantId === entityId && tenant.id === entityId;

    if (!isCurrentTenant) {
      // always show the crumb (with icon); leave title blank to avoid repeating the crumb label
      return of({ items: [organizationsCrumb], title: '' });
    }

    if (!tenant.parent) {
      return this.getSubOrgCrumbChildren$(tenant.id).pipe(
        map((children) => ({
          // only add a dedicated item (with dropdown) when there are sub-orgs to switch between,
          // otherwise the title crumb already shows the org name and a second item would duplicate it
          items:
            children.length > 0
              ? [organizationsCrumb, { label: tenant.name, id: `/organizations/${tenant.id}`, children }]
              : [organizationsCrumb],
          title: tenant.name,
        })),
      );
    }

    return combineLatest([
      // getTenantDetail$ rethrows through DataService.handleError$; fall back to skipping the parent crumb
      // rather than letting a transient failure error out the shared breadcrumb stream
      this.dataService.getTenantDetail$(tenant.parent).pipe(catchError(() => of(undefined))),
      this.getSubOrgCrumbChildren$(tenant.parent),
    ]).pipe(
      map(([parentTenant, children]) => ({
        items: parentTenant
          ? [
              organizationsCrumb,
              { label: parentTenant.name, id: `/organizations/${tenant.parent}`, children },
              { label: tenant.name, id: `/organizations/${tenant.id}` },
            ]
          : [organizationsCrumb, { label: tenant.name, id: `/organizations/${tenant.id}` }],
        title: tenant.name,
      })),
    );
  }

  // builds the dropdown shown under the parent org crumb, listing its sub-organizations
  private getSubOrgCrumbChildren$(parentTenantId: string): Observable<IBreadcrumbItem[]> {
    return this.dataService
      .getSubOrgList$(
        { tenantId: parentTenantId, perPage: SUB_ORG_PAGE_SIZE, pageNum: 1 },
        { sortModel: [], filterModel: {} },
        true,
      )
      .pipe(
        map((subOrgs) =>
          subOrgs.tenants.records
            .map((subOrg) => ({ label: subOrg.name, id: `/${AdminSection.Organizations}/${subOrg.id}` }))
            .sort((a, b) => a.label.localeCompare(b.label)),
        ),
        // getSubOrgList$ rethrows through DataService.handleError$; fall back to no dropdown
        // rather than letting a transient failure error out the shared breadcrumb stream
        catchError(() => of([])),
      );
  }
}
